import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FieldContainer,
  Label,
  Required,
  InputWrapper,
  SearchInput,
  ChevronIcon,
  Dropdown,
  DropdownOption,
  CreateOption,
  CreateIcon,
  NoResults,
  Spinner,
  HelperText,
  CheckIcon,
} from './StyledComponents';

const ChevronSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * CreatableSelect - A select dropdown that allows creating new options
 *
 * @param {string} label - Label text for the select
 * @param {boolean} required - Whether the field is required (shows red asterisk)
 * @param {string|number} value - The selected value
 * @param {Function} onChange - Callback when value changes (receives selected option object)
 * @param {string} placeholder - Placeholder text for search input
 * @param {Array} options - Array of {label, value} objects for options
 * @param {Function} onCreate - Async callback when creating new option (receives label string, should return new option object)
 * @param {string} createLabel - Label for create option (default: "Create")
 * @param {string} helperText - Helper/error text below the select
 * @param {boolean} error - Whether the select has an error state
 * @param {string} width - Width of the select (default: "100%")
 * @param {string} maxWidth - Max width of the select
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} noResultsText - Text to show when no results
 * @param {number} minCreateLength - Minimum characters required to show create option (default: 1)
 */
const CreatableSelect = ({
  label,
  required = false,
  value,
  onChange,
  placeholder = 'Search or create...',
  options = [],
  onCreate,
  createLabel = 'Create',
  helperText,
  error = false,
  width = '100%',
  maxWidth,
  disabled = false,
  noResultsText = 'No options found',
  minCreateLength = 1,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // Find the selected option to display its label
  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  // Filter options based on search text
  const filteredOptions = searchText
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
      )
    : options;

  // Check if there's an exact match (case-insensitive)
  const exactMatch = options.some(
    (opt) => opt.label.toLowerCase() === searchText.toLowerCase()
  );

  // Should show create option?
  const showCreateOption =
    onCreate &&
    searchText.length >= minCreateLength &&
    !exactMatch &&
    !isLoading;

  // Clear any pending close timeout
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Open dropdown
  const open = useCallback(() => {
    if (disabled) return;
    clearCloseTimeout();
    setIsOpen(true);
    setHighlightedIndex(-1);
  }, [disabled, clearCloseTimeout]);

  // Close dropdown
  const close = useCallback(() => {
    if (isLoading) return;
    setIsOpen(false);
    setSearchText('');
    setHighlightedIndex(-1);
  }, [isLoading]);

  // Schedule close (for blur events)
  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!isLoading) {
        close();
      }
    }, 200);
  }, [close, isLoading, clearCloseTimeout]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        if (!isLoading) {
          close();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close, isLoading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleInputChange = (e) => {
    setSearchText(e.target.value);
    setHighlightedIndex(-1);
    if (!isOpen) {
      open();
    }
  };

  const handleInputFocus = () => {
    open();
  };

  const handleSelectOption = useCallback(
    (option) => {
      onChange(option);
      setSearchText('');
      close();
    },
    [onChange, close]
  );

  const handleCreate = useCallback(async () => {
    if (!onCreate || !searchText.trim() || isLoading) return;

    const labelToCreate = searchText.trim();
    setIsLoading(true);

    try {
      const newOption = await onCreate(labelToCreate);
      if (newOption) {
        onChange(newOption);
      }
    } catch (err) {
      // Error handling is left to the parent component via onCreate
      console.error('Failed to create option:', err);
    } finally {
      setIsLoading(false);
      setSearchText('');
      close();
    }
  }, [onCreate, searchText, isLoading, onChange, close]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        open();
      }
      return;
    }

    const totalItems = filteredOptions.length + (showCreateOption ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredOptions.length) {
            handleSelectOption(filteredOptions[highlightedIndex]);
          } else if (showCreateOption) {
            handleCreate();
          }
        } else if (showCreateOption && filteredOptions.length === 0) {
          // If no options and create is available, create on Enter
          handleCreate();
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  const handleDropdownMouseDown = (e) => {
    // Prevent blur from firing when clicking dropdown
    e.preventDefault();
  };

  // Display value: show search text when open, otherwise show selected option label
  const displayValue = isOpen ? searchText : selectedOption?.label || '';

  return (
    <FieldContainer width={width} maxWidth={maxWidth} ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}

      <InputWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={scheduleClose}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          error={error}
          isOpen={isOpen}
          autoComplete="off"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />

        <ChevronIcon isOpen={isOpen}>
          <ChevronSvg />
        </ChevronIcon>

        {isOpen && (
          <Dropdown role="listbox" onMouseDown={handleDropdownMouseDown}>
            {filteredOptions.length === 0 && !showCreateOption && !isLoading ? (
              <NoResults>
                {searchText ? noResultsText : 'Type to search...'}
              </NoResults>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const isSelected = String(option.value) === String(value);
                  const isHighlighted = highlightedIndex === index;

                  return (
                    <DropdownOption
                      key={option.value}
                      isSelected={isSelected}
                      isHighlighted={isHighlighted}
                      onClick={() => handleSelectOption(option)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {option.label}
                      {isSelected && (
                        <CheckIcon>
                          <CheckSvg />
                        </CheckIcon>
                      )}
                    </DropdownOption>
                  );
                })}

                {isLoading && (
                  <CreateOption disabled>
                    <span>Creating "{searchText}"...</span>
                    <Spinner />
                  </CreateOption>
                )}

                {showCreateOption && (
                  <CreateOption
                    onClick={handleCreate}
                    isHighlighted={highlightedIndex === filteredOptions.length}
                  >
                    <span>
                      <CreateIcon>+</CreateIcon>
                      {createLabel} "{searchText}"
                    </span>
                  </CreateOption>
                )}
              </>
            )}
          </Dropdown>
        )}
      </InputWrapper>

      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FieldContainer>
  );
};

CreatableSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  onCreate: PropTypes.func,
  createLabel: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  disabled: PropTypes.bool,
  noResultsText: PropTypes.string,
  minCreateLength: PropTypes.number,
};

export default CreatableSelect;
